/**
 * Labstep
 *
 * @module screens/ResourceItem/Index
 * @desc Resource Item Index
 */

import type { EntitySearchContainerChildrenProps } from 'labstep-web/components/Entity/Search/Children/types';
import { EntityImportModalTrigger as EntityImportModal } from 'labstep-web/components/EntityImport/Modal/Trigger';
import ResourceItemActionCreate from 'labstep-web/components/ResourceItem/Action/Create';
import { ResourceItemEmptyState } from 'labstep-web/components/ResourceItem/EmptyState';
import { ParamsStateContextProvider } from 'labstep-web/contexts/params-state';
import type { ParamsStateContextType } from 'labstep-web/contexts/params-state/types';
import { ExportProgressProvider } from 'labstep-web/grid/Index/components/DataGridExportAsCSV/context';
import { GridNewIndex } from 'labstep-web/grid/NewIndex';
import { getSystemFilterResourceResourceTemplateGuid } from 'labstep-web/grid/NewIndex/services/grid-new-index.service';
import { GridFilterService } from 'labstep-web/grid/services/grid-filter.service';
import { EntityView } from 'labstep-web/models/entity-view.model';
import { ResourceItem } from 'labstep-web/models/resource-item.model';
import { MasterIndex } from 'labstep-web/screens/Master/Index';
import { ResourceItemIndexBreadcrumb } from 'labstep-web/screens/ResourceItem/Index/Breadcrumb';
import { ResourceItemIndexLeftPane } from 'labstep-web/screens/ResourceItem/Index/LeftPane';
import type React from 'react';
import { useMemo } from 'react';
import { bulkActions, filters, sortOptions } from './constants';
import type { ScreensResourceItemIndexMasterProps } from './types';

export const ScreensResourceItemIndexMaster: React.FC<
  ScreensResourceItemIndexMasterProps
> = ({ entityView, resourceTemplate, group, links }) => {
  const breadcrumb = <ResourceItemIndexBreadcrumb />;

  const action = (
    <ParamsStateContextProvider value={{ isGrid: true }}>
      <ResourceItemActionCreate
        withRedirect={false}
        resourceTemplate={resourceTemplate}
        extraOptions={
          <EntityImportModal
            targetEntityName={ResourceItem.entityName}
            templateId={resourceTemplate?.id}
          />
        }
      />
    </ParamsStateContextProvider>
  );

  const noResultsMessage = (
    <ParamsStateContextProvider value={{ isGrid: true }}>
      <ResourceItemEmptyState
        action={
          <ResourceItemActionCreate
            withRedirect={false}
            resourceTemplate={resourceTemplate}
          />
        }
        secondaryAction={
          <EntityImportModal
            targetEntityName={ResourceItem.entityName}
            templateId={resourceTemplate?.id}
            actionComponentProps={{
              type: 'button',
              elementProps: { basic: true },
            }}
          />
        }
      />
    </ParamsStateContextProvider>
  );

  const leftPane = useMemo(
    () => <ResourceItemIndexLeftPane links={links} />,
    [links],
  );

  const globalParams: Record<
    string,
    string | number | Record<string, unknown>[]
  > = {
    group_id: group.id,
    skip_total: 1,
  };

  const paramsStateContext: ParamsStateContextType = {
    isGrid: true,
    name: 'resource_item_index',
    globalParams,
    entityView,
    loadedEntityView: new EntityView({
      ...entityView,
    }),
  };
  if (resourceTemplate) {
    paramsStateContext.initialSearchParams =
      GridFilterService.injectSystemFilter(
        getSystemFilterResourceResourceTemplateGuid(
          resourceTemplate.guid,
        ),
        entityView.parameters || {},
      );
  } else {
    paramsStateContext.initialSearchParams =
      entityView.parameters || {};
  }
  if (paramsStateContext.loadedEntityView) {
    paramsStateContext.loadedEntityView.parameters =
      paramsStateContext.initialSearchParams;
  }
  const params = useMemo(
    () => ({
      ...paramsStateContext.initialSearchParams,
    }),
    [group.id, entityView.parameters],
  );

  return (
    <ParamsStateContextProvider value={paramsStateContext}>
      <ExportProgressProvider>
        <MasterIndex
          entityName={ResourceItem.entityName}
          breadcrumb={breadcrumb}
          leftPane={leftPane}
          filters={filters}
          params={params}
          noResultsMessage={noResultsMessage}
          action={action}
          bulkActions={bulkActions}
          sortOptions={sortOptions}
          useAllPages
          usePostFilter
          tableFormat={false}
        >
          {({
            entities,
            readNextPage,
            status,
          }: EntitySearchContainerChildrenProps) => (
            <GridNewIndex
              entityName={ResourceItem.entityName}
              group={group}
              entities={entities}
              entityView={entityView}
              status={status}
              loadMore={readNextPage}
            />
          )}
        </MasterIndex>
      </ExportProgressProvider>
    </ParamsStateContextProvider>
  );
};
